import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EmcProductComplianceFileDeclarationOfConformityGenerateDialog } from '@/v2/components/emc/EmcProductComplianceFileDeclarationOfConformityGenerateDialog'
import { V2ProductDescriptionEditDialog } from '@/v2/components/rohs/V2ProductDescriptionEditDialog'
import { V2EmcApplicabilityStep } from '@/v2/components/emc-legislation-briefing/V2EmcApplicabilityStep'
import { V2EmcTechnicalDrawingsSlotStep } from '@/v2/components/emc-legislation-briefing/V2EmcTechnicalDrawingsSlotStep'
import { V2EmcComplianceFileSlotStep } from '@/v2/components/emc-legislation-briefing/V2EmcComplianceFileSlotStep'
import { V2EmcConfirmedStandardsEditSheet } from '@/v2/components/emc-legislation-briefing/V2EmcConfirmedStandardsEditSheet'
import { V2EmcConfirmedStandardsStep } from '@/v2/components/emc-legislation-briefing/V2EmcConfirmedStandardsStep'
import { evaluateEmcProductComplianceFileDeclarationOfConformityRequirements } from '@/v2/lib/emcProductComplianceFileDeclarationOfConformityRequirements'
import { V2EmcEuDeclarationStep } from '@/v2/components/emc-legislation-briefing/V2EmcEuDeclarationStep'
import { V2EmcManufacturerInformationStep } from '@/v2/components/emc-legislation-briefing/V2EmcManufacturerInformationStep'
import { V2EmcProductDescriptionStep } from '@/v2/components/emc-legislation-briefing/V2EmcProductDescriptionStep'
import { V2EmcProductIdentificationStep } from '@/v2/components/emc-legislation-briefing/V2EmcProductIdentificationStep'
import { V2EmcProductSignOffStep } from '@/v2/components/emc-legislation-briefing/V2EmcProductSignOffStep'
import { V2EmcRequiredNextBanner } from '@/v2/components/emc-legislation-briefing/V2EmcRequiredNextBanner'
import { useGetEmcProductComplianceFiles } from '@/shared/hooks/api/emcComplianceFiles'
import {
  isEmcComplianceFileSlotFilled,
  useGetEmcComplianceFileSlot,
} from '@/shared/hooks/api/emcProductComplianceFileAttachments'
import { useGetProduct } from '@/shared/hooks/api/product'
import {
  useGetProductSharedEvidence,
  isProductSharedEvidenceSlotFilled,
} from '@/shared/hooks/api/productSharedEvidence'
import { useCanWriteOrgData } from '@/shared/hooks/useCanWriteOrgData'
import { BriefingStepList } from '@/shared/briefing'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import {
  buildEmcLegislationBriefing,
  findFirstIncompleteRequiredStep,
  parseConfirmedEmcStandards,
} from '../utils/emcLegislationBriefing'

export function V2EmcLegislationBriefing({
  productId,
  emcApplies,
}: {
  productId: string
  emcApplies: boolean
}) {
  const { t } = useTranslation('common')
  const { data: product } = useGetProduct(productId)
  const { data: sharedEvidence } = useGetProductSharedEvidence(productId)
  const { data: drawings } = useGetEmcComplianceFileSlot(
    productId,
    'DRAWINGS_SCHEMAS',
    { enabled: emcApplies }
  )
  const { data: testReportDisturbance } = useGetEmcComplianceFileSlot(
    productId,
    'TEST_REPORT_ELECTROMAGNETIC_DISTURBANCE',
    { enabled: emcApplies }
  )
  const { data: testReportImmunity } = useGetEmcComplianceFileSlot(
    productId,
    'TEST_REPORT_ELECTROMAGNETIC_IMMUNITY',
    { enabled: emcApplies }
  )
  const { data: descriptionsAndExplanations } = useGetEmcComplianceFileSlot(
    productId,
    'DESCRIPTIONS_AND_EXPLANATIONS',
    { enabled: emcApplies }
  )
  const {
    data: files,
    isLoading,
    isError,
  } = useGetEmcProductComplianceFiles(productId, {
    enabled: emcApplies,
  })
  const canWrite = useCanWriteOrgData()
  const [descriptionDialogOpen, setDescriptionDialogOpen] = useState(false)
  const [confirmedStandardsSheetOpen, setConfirmedStandardsSheetOpen] =
    useState(false)
  const [declarationDialogOpen, setDeclarationDialogOpen] = useState(false)
  const [declarationProgress, setDeclarationProgress] = useState<
    string | null
  >(null)

  if (!emcApplies) {
    return null
  }

  if (isLoading) {
    return (
      <div className='space-y-3' aria-busy='true'>
        <Skeleton className='h-20 w-full' />
        <Skeleton className='h-40 w-full' />
      </div>
    )
  }

  if (isError || files == null) {
    return (
      <p className='text-sm text-muted-foreground'>
        {t('v2.pages.products.detail.emcFiles.loadFailed')}
      </p>
    )
  }

  const hasProductDescription =
    product?.description != null && product.description.trim() !== ''
  const briefing = buildEmcLegislationBriefing({
    hasProductDescription,
    hasProductIdentification: isProductSharedEvidenceSlotFilled(
      sharedEvidence,
      'PRODUCT_IDENTIFICATION'
    ),
    hasManufacturerInformation: isProductSharedEvidenceSlotFilled(
      sharedEvidence,
      'MANUFACTURER_INFORMATION'
    ),
    slotFilled: {
      DRAWINGS_SCHEMAS: isEmcComplianceFileSlotFilled(drawings),
      TEST_REPORT_ELECTROMAGNETIC_DISTURBANCE:
        isEmcComplianceFileSlotFilled(testReportDisturbance),
      TEST_REPORT_ELECTROMAGNETIC_IMMUNITY:
        isEmcComplianceFileSlotFilled(testReportImmunity),
      DESCRIPTIONS_AND_EXPLANATIONS: isEmcComplianceFileSlotFilled(
        descriptionsAndExplanations
      ),
    },
    files,
  })
  const firstIncompleteRequiredStep = findFirstIncompleteRequiredStep(
    briefing.steps
  )
  const productFile = files.product
  const confirmedStandards = parseConfirmedEmcStandards(
    productFile?.confirmedStandardsJson ?? null
  )
  const declarationEvaluation =
    productFile != null
      ? evaluateEmcProductComplianceFileDeclarationOfConformityRequirements({
          productName: product?.name,
          productSku: product?.sku,
          productDescription: product?.description,
          emcApplies,
          productFile,
        })
      : null
  let declarationLabel = t(
    'v2.pages.products.detail.emcBriefing.steps.items.euDeclaration.action'
  )
  if (declarationProgress != null) {
    declarationLabel = declarationProgress
  }

  const [
    applicability,
    productDescription,
    productIdentification,
    manufacturerInformation,
    confirmedStandardsStep,
    productDrawings,
    testReportElectromagneticDisturbance,
    testReportElectromagneticImmunity,
    euDeclaration,
    productSignOff,
  ] = briefing.steps

  return (
    <div className='space-y-6'>
      <V2EmcRequiredNextBanner
        firstIncompleteStep={firstIncompleteRequiredStep}
      />

      <Alert variant='info'>
        <AlertTitle className='line-clamp-none'>
          {t('v2.pages.products.detail.emcBriefing.overview.title')}
        </AlertTitle>
        <AlertDescription className='space-y-2'>
          <p>{t('v2.pages.products.detail.emcBriefing.overview.body')}</p>
        </AlertDescription>
      </Alert>

      <BriefingStepList
        title={t('v2.pages.products.detail.emcBriefing.steps.title')}
        description={t('v2.pages.products.detail.emcBriefing.steps.description')}
      >
        <V2EmcApplicabilityStep step={applicability} index={0} />
        <V2EmcProductDescriptionStep
          step={productDescription}
          index={1}
          description={product?.description}
          hasProductDescription={hasProductDescription}
          canWrite={canWrite}
          onEditDescription={() => {
            setDescriptionDialogOpen(true)
          }}
        />
        <V2EmcProductIdentificationStep
          step={productIdentification}
          index={2}
          productId={productId}
        />
        <V2EmcManufacturerInformationStep
          step={manufacturerInformation}
          index={3}
          productId={productId}
        />
        <V2EmcConfirmedStandardsStep
          step={confirmedStandardsStep}
          index={4}
          standards={confirmedStandards}
          canWrite={canWrite}
          onEditStandards={() => {
            setConfirmedStandardsSheetOpen(true)
          }}
        />
        <V2EmcTechnicalDrawingsSlotStep
          step={productDrawings}
          index={5}
          productId={productId}
        />
        <V2EmcComplianceFileSlotStep
          step={testReportElectromagneticDisturbance}
          index={6}
          productId={productId}
          slotKind='TEST_REPORT_ELECTROMAGNETIC_DISTURBANCE'
        />
        <V2EmcComplianceFileSlotStep
          step={testReportElectromagneticImmunity}
          index={7}
          productId={productId}
          slotKind='TEST_REPORT_ELECTROMAGNETIC_IMMUNITY'
        />
        <V2EmcEuDeclarationStep
          step={euDeclaration}
          index={8}
          declarationLabel={declarationLabel}
          declarationEvaluation={declarationEvaluation}
          onOpenDeclarationDialog={() => {
            setDeclarationDialogOpen(true)
          }}
        />
        <V2EmcProductSignOffStep
          step={productSignOff}
          index={9}
          productId={productId}
          productFile={productFile}
        />
      </BriefingStepList>
      <V2ProductDescriptionEditDialog
        productId={productId}
        description={product?.description}
        open={descriptionDialogOpen}
        onOpenChange={setDescriptionDialogOpen}
      />
      {confirmedStandardsSheetOpen ? (
        <V2EmcConfirmedStandardsEditSheet
          productId={productId}
          standards={confirmedStandards}
          open={confirmedStandardsSheetOpen}
          onOpenChange={setConfirmedStandardsSheetOpen}
        />
      ) : null}
      {productFile != null ? (
        <EmcProductComplianceFileDeclarationOfConformityGenerateDialog
          open={declarationDialogOpen}
          onOpenChange={setDeclarationDialogOpen}
          productId={productId}
          productName={product?.name}
          productSku={product?.sku}
          productDescription={product?.description}
          emcApplies={emcApplies}
          productFile={productFile}
          outputDocuments={files.outputDocuments}
          onProgressMessage={setDeclarationProgress}
        />
      ) : null}
    </div>
  )
}
